import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { AlertOctagon } from 'lucide-react';

// ── FSPIOP ──────────────────────────────────────────────────────────────────
const FSPIOP_CATEGORIES = [
  ['1xxx', 'Communication', 'Network / destination unreachable.'],
  ['2xxx', 'Server', 'Internal error, not implemented, unavailable, timed out.'],
  ['3xxx', 'Client / validation', 'Malformed, missing element, bad signature, not found, expired.'],
  ['4xxx', 'Payer', 'Payer FSP liquidity, rejection, limit, permission, blocked.'],
  ['5xxx', 'Payee', 'Payee FSP liquidity, rejection, limit, permission, blocked.'],
];

const FSPIOP_CODES = [
  ['3100', 'Generic validation error', '3xxx'],
  ['3101', 'Malformed syntax', '3xxx'],
  ['3102', 'Missing mandatory element', '3xxx'],
  ['3105', 'Invalid signature', '3xxx'],
  ['3106', 'Modified request', '3xxx'],
  ['3200', 'Generic ID not found', '3xxx'],
  ['3204', 'Party not found', '3xxx'],
  ['3208', 'Transfer ID not found', '3xxx'],
  ['3302', 'Quote expired', '3xxx'],
  ['3303', 'Transfer expired', '3xxx'],
  ['4001', 'Payer FSP insufficient liquidity', '4xxx'],
  ['4101', 'Payer rejected transaction request', '4xxx'],
  ['4200', 'Payer limit error', '4xxx'],
  ['5000', 'Generic payee error', '5xxx'],
  ['5001', 'Payee FSP insufficient liquidity', '5xxx'],
  ['5101', 'Payee rejected quote', '5xxx'],
  ['5104', 'Payee rejected transaction', '5xxx'],
  ['5200', 'Payee limit error', '5xxx'],
];

// ── ISO 20022 ───────────────────────────────────────────────────────────────
const ISO_STATUS = [
  ['AC01', 'Incorrect account number'],
  ['AC04', 'Closed account number'],
  ['AC06', 'Blocked account'],
  ['AG01', 'Transaction forbidden'],
  ['AM04', 'Insufficient funds'],
  ['AM05', 'Duplication'],
  ['BE01', 'Inconsistent with end customer'],
  ['MD07', 'End customer deceased'],
  ['MS03', 'Reason not specified (agent)'],
  ['NARR', 'Narrative (free-text reason in AddtlInf)'],
  ['RC01', 'Bank identifier incorrect'],
  ['TM01', 'Cut-off time'],
];

const ISO_CANCEL = [
  ['DUPL', 'Duplicate payment'],
  ['TECH', 'Technical problem'],
  ['FRAD', 'Fraudulent origin'],
  ['CUST', 'Requested by customer'],
  ['CUTA', 'Cancel upon unable to apply'],
  ['UPAY', 'Undue payment'],
  ['AGNT', 'Incorrect agent'],
  ['CURR', 'Incorrect currency'],
];

function CodeTable({ rows, cols }) {
  return (
    <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
      <table className="w-full text-sm">
        <tbody>
          {rows.map((r, i) => (
            <tr key={r[0]} className={i % 2 ? 'bg-gray-50' : ''}>
              {r.map((cell, j) => (
                <td key={j} className={`px-4 py-2 align-top ${j === 0 ? 'font-mono text-xs text-primary-700 whitespace-nowrap w-20' : 'text-gray-700'} ${cols && j === cols - 1 ? 'text-xs text-gray-400 w-16' : ''}`}>
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function ErrorReference() {
  const location = useLocation();
  const isIso = location.pathname.startsWith('/iso20022');
  const [view, setView] = useState(isIso ? 'iso' : 'fspiop');

  return (
    <div className="space-y-8 max-w-4xl">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <AlertOctagon className="w-6 h-6 text-primary-600" />
          Error &amp; Reason Codes
        </h1>
        <p className="text-sm text-gray-500 mt-1">
          What the codes mean and how to respond — FSPIOP error objects and ISO 20022 reason codes.
        </p>
      </div>

      {/* toggle */}
      <div className="flex rounded-lg border border-gray-200 overflow-hidden text-sm font-medium w-fit">
        {[['iso', 'ISO 20022'], ['fspiop', 'FSPIOP']].map(([id, label]) => (
          <button key={id} onClick={() => setView(id)}
            className={`px-4 py-1.5 transition-colors ${view === id ? 'bg-primary-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}>
            {label}
          </button>
        ))}
      </div>

      {view === 'fspiop' ? (
        <>
          <section>
            <h2 className="text-lg font-semibold text-gray-900 mb-3">Error object</h2>
            <p className="text-sm text-gray-600 mb-3">
              FSPIOP errors are returned as an <span className="font-mono text-xs">errorInformation</span> object.
              The code is a 4-digit number; the first digit is the category.
            </p>
            <pre className="bg-gray-900 text-gray-100 text-xs rounded-lg p-4 overflow-x-auto">{`{
  "errorInformation": {
    "errorCode": "5101",
    "errorDescription": "Payee rejected quote",
    "extensionList": { "extension": [ { "key": "cause", "value": "…" } ] }
  }
}`}</pre>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-gray-900 mb-3">Categories</h2>
            <CodeTable rows={FSPIOP_CATEGORIES} />
          </section>

          <section>
            <h2 className="text-lg font-semibold text-gray-900 mb-3">Common codes</h2>
            <CodeTable rows={FSPIOP_CODES} cols={3} />
            <p className="text-sm text-gray-500 mt-2">
              Respond to 3xxx by fixing the request (it will fail identically on retry). 4xxx/5xxx are
              business outcomes — surface them to the customer; only retry if the cause is transient
              (e.g. 4001/5001 liquidity).
            </p>
          </section>
        </>
      ) : (
        <>
          <section>
            <h2 className="text-lg font-semibold text-gray-900 mb-3">Where reason codes appear</h2>
            <p className="text-sm text-gray-600 mb-3">
              The same external code lists drive the reason fields across message types:
            </p>
            <CodeTable rows={[
              ['pacs.002', 'Rejection — TxInfAndSts / StsRsnInf / Rsn / Cd'],
              ['camt.056', 'Cancellation — Undrlyg / TxInf / CxlRsnInf / Rsn / Cd'],
              ['pacs.004', 'Return — TxInf / RtrRsnInf / Rsn / Cd'],
            ]} />
          </section>

          <section>
            <h2 className="text-lg font-semibold text-gray-900 mb-3">Status / rejection reasons</h2>
            <p className="text-sm text-gray-600 mb-3">Used in pacs.002 RJCT and pacs.004 returns.</p>
            <CodeTable rows={ISO_STATUS} />
          </section>

          <section>
            <h2 className="text-lg font-semibold text-gray-900 mb-3">Cancellation reasons</h2>
            <p className="text-sm text-gray-600 mb-3">Used in camt.056 cancellation requests.</p>
            <CodeTable rows={ISO_CANCEL} />
            <p className="text-sm text-gray-500 mt-2">
              When no code fits, use <span className="font-mono text-xs">NARR</span> and put the
              free-text explanation in <span className="font-mono text-xs">AddtlInf</span>.
            </p>
          </section>
        </>
      )}
    </div>
  );
}
